import React, { useState, useEffect } from 'react';
import { API, containsProhibitedContent } from '../../utils/api';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

/**
 * SearchControls - Advanced Research Platform & Information Exchange Network
 * REORIENTED: Educated Individual Sites is the PRIMARY hub.
 * Certification filters are secondary, unchecked by default, collapsed.
 * All controls have ocean-shimmer effects.
 */

/** Sparkle dots for shimmer sections */
const SparkleOverlay = ({ count = 6 }) => (
  <div className="sparkle-container">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className="sparkle-dot" style={{
        top: `${10 + Math.random() * 80}%`,
        left: `${5 + Math.random() * 90}%`,
        animationDelay: `${Math.random() * 4}s`,
        animationDuration: `${2 + Math.random() * 3}s`,
        width: `${2 + Math.random() * 2}px`,
        height: `${2 + Math.random() * 2}px`,
        background: ['rgba(253, 230, 138,0.7)', 'rgba(252, 211, 77,0.7)', 'rgba(16,185,129,0.6)', 'rgba(251,191,36,0.5)'][i % 4]
      }} />
    ))}
  </div>
);

/** Collapsible Certification Filters — SECONDARY, collapsed by default, unchecked by default */
const CertificationFiltersCollapsible = ({ certFilters, setCertFilters, toggleCertFilter }) => {
  const [expanded, setExpanded] = useState(false);
  const activeCount = ['pearsonvue', 'government', 'advancedDegree'].filter(k => certFilters[k]).length;

  return (
    <div className="ocean-shimmer shimmer-border" style={{
      marginBottom: 10, borderRadius: 10,
      border: '1px solid rgba(239, 68, 68, 0.15)', overflow: 'hidden',
      background: 'rgba(239, 68, 68, 0.03)'
    }}>
      <button onClick={() => setExpanded(!expanded)} data-testid="cert-filters-toggle"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#a1a1aa'
        }}>
        <span style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="fas fa-certificate" style={{ fontSize: '0.7rem', color: '#ef4444' }} />
          Certification Filters
          <span style={{ fontSize: '0.62rem', color: '#71717a', background: 'rgba(239,68,68,0.1)', padding: '1px 6px', borderRadius: 4 }}>
            {activeCount}/3 active &middot; paid access programs
          </span>
        </span>
        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} style={{ fontSize: '0.6rem' }} />
      </button>
      {expanded && (
        <div style={{ padding: '8px 14px 12px', position: 'relative' }}>
          <SparkleOverlay count={3} />
          <p style={{ color: '#71717a', fontSize: '0.68rem', marginBottom: 8, lineHeight: 1.4 }}>
            Enable these to include URLs with quick blurbs and immediate paid access to degree &amp; certification programs.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {[
              { key: 'pearsonvue', label: 'PearsonVUE', color: '#FDE047' },
              { key: 'government', label: 'Government', color: '#ef4444' },
              { key: 'advancedDegree', label: 'Advanced Degree', color: '#3b82f6' }
            ].map(f => (
              <label key={f.key} className={`filter-pill ${certFilters[f.key] ? 'filter-pill-active' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                  background: certFilters[f.key] ? `${f.color}20` : 'rgba(255, 255, 255,0.4)',
                  border: certFilters[f.key] ? `1px solid ${f.color}50` : '1px solid rgba(203, 213, 225, 0.8)'
                }} data-testid={`cert-filter-${f.key}`}>
                <input type="checkbox" checked={certFilters[f.key]} onChange={() => toggleCertFilter(f.key)}
                  style={{ accentColor: f.color, width: 14, height: 14, cursor: 'pointer' }} />
                <span style={{ color: certFilters[f.key] ? '#fff' : '#a1a1aa', fontSize: '0.78rem', fontWeight: certFilters[f.key] ? 600 : 400 }}>{f.label}</span>
              </label>
            ))}
            <button onClick={() => setCertFilters(prev => ({ ...prev, pearsonvue: true, government: true, advancedDegree: true }))}
              className="btn-ocean" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', padding: '4px 10px', borderRadius: 6, fontSize: '0.68rem', cursor: 'pointer', marginLeft: 'auto' }}
              data-testid="select-all-certs">All</button>
            <button onClick={() => setCertFilters(prev => ({ ...prev, pearsonvue: false, government: false, advancedDegree: false }))}
              className="btn-ocean" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '4px 10px', borderRadius: 6, fontSize: '0.68rem', cursor: 'pointer' }}
              data-testid="clear-all-certs">Clear All</button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Collapsible Content Preference Filters */
const ContentPreferenceFiltersCollapsible = ({ certFilters, setCertFilters, toggleCertFilter }) => {
  const [expanded, setExpanded] = useState(false);
  const prefKeys = ['schematics', 'pearsonvueCert', 'engineering', 'computerScience', 'physicsChemistry', 'economicsFinance', 'astronomy', 'newsWithMaps'];
  const activeCount = prefKeys.filter(k => certFilters[k]).length;

  return (
    <div className="ocean-shimmer shimmer-border" style={{
      marginBottom: 12, borderRadius: 10,
      border: '1px solid rgba(16,185,129,0.15)', overflow: 'hidden',
      background: 'rgba(16,185,129,0.04)'
    }}>
      <button onClick={() => setExpanded(!expanded)} data-testid="content-pref-toggle"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#a1a1aa'
        }}>
        <span style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="fas fa-sliders-h" style={{ fontSize: '0.7rem', color: '#10b981' }} />
          Content Preference Filters
          <span style={{ fontSize: '0.68rem', color: '#71717a' }}>({activeCount}/{prefKeys.length} active)</span>
        </span>
        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} style={{ fontSize: '0.6rem' }} />
      </button>
      {expanded && (
        <div style={{ padding: '8px 14px 12px', position: 'relative' }}>
          <SparkleOverlay count={4} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              { key: 'schematics', label: 'Schematics & Diagrams', color: '#10b981' },
              { key: 'pearsonvueCert', label: 'PearsonVUE Cert', color: '#FDE047' },
              { key: 'engineering', label: 'Engineering', color: '#f59e0b' },
              { key: 'computerScience', label: 'Computer Science', color: '#3b82f6' },
              { key: 'physicsChemistry', label: 'Physics & Chemistry', color: '#047857' },
              { key: 'economicsFinance', label: 'Economics & Finance', color: '#22c55e' },
              { key: 'astronomy', label: 'Astronomy', color: '#FCD34D' },
              { key: 'newsWithMaps', label: 'News with Maps', color: '#ef4444' }
            ].map(f => (
              <label key={f.key} className={`filter-pill ${certFilters[f.key] ? 'filter-pill-active' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                  background: certFilters[f.key] ? `${f.color}18` : 'rgba(255, 255, 255,0.4)',
                  border: certFilters[f.key] ? `1px solid ${f.color}40` : '1px solid rgba(203, 213, 225, 0.8)'
                }} data-testid={`content-filter-${f.key}`}>
                <input type="checkbox" checked={certFilters[f.key] || false} onChange={() => toggleCertFilter(f.key)}
                  style={{ accentColor: f.color, width: 14, height: 14, cursor: 'pointer' }} />
                <span style={{ color: certFilters[f.key] ? '#fff' : '#a1a1aa', fontSize: '0.75rem', fontWeight: certFilters[f.key] ? 600 : 400 }}>{f.label}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setCertFilters(prev => ({ ...prev, schematics: true, pearsonvueCert: true, engineering: true, computerScience: true, physicsChemistry: true, economicsFinance: true, astronomy: true, newsWithMaps: true }))}
              className="btn-ocean" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', padding: '4px 10px', borderRadius: 6, fontSize: '0.68rem', cursor: 'pointer' }}
              data-testid="select-all-content-filters">All</button>
            <button onClick={() => setCertFilters(prev => ({ ...prev, schematics: false, pearsonvueCert: false, engineering: false, computerScience: false, physicsChemistry: false, economicsFinance: false, astronomy: false, newsWithMaps: false }))}
              className="btn-ocean" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '4px 10px', borderRadius: 6, fontSize: '0.68rem', cursor: 'pointer' }}
              data-testid="clear-content-filters">Clear</button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Rich info tooltip using Shadcn Popover — replaces native tooltip */
const InfoTooltip = ({ text }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="info-tooltip-trigger"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'help',
            display: 'inline-flex', alignItems: 'center', marginLeft: 4
          }}
        >
          <i className="fas fa-question-circle" style={{ fontSize: '0.65rem', color: '#6b7280' }} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="z-[100] max-w-[280px] border-purple-500/40 bg-[rgba(253, 251, 247,0.97)] text-zinc-300 text-xs leading-relaxed shadow-xl"
        sideOffset={8}
      >
        {text}
      </PopoverContent>
    </Popover>
  );
};

const SEARCH_INFO = {
  quickSearch: 'Quick Search performs a fast multi-engine search (Google, Bing, DuckDuckGo, Brave) and returns results directly in a URL Collection Window. Best for quick lookups.',
  searchCategorize: 'Search & Categorize searches across all engines AND assigns results to your selected categories. If no categories selected, auto-assigns to all matching categories.',
  searchEngineAccess: 'Search Engine Access queries all available search engines simultaneously and displays raw results in a dedicated panel. Fastest way to see engine-specific results.',
  autoCategorize: 'Auto-Categorize All runs a deep search with GPT-expanded queries, then automatically categorizes ALL results into your existing categories. Great for building your knowledge base.',
  aiSearch: 'AI Intelligent Search uses GPT to expand your query into multiple research angles, then searches comprehensively. Choose Comprehensive, News, or Research focus modes.',
  comprehensive: 'Comprehensive mode generates 15-25 search variations covering tutorials, expert guides, certifications, tools, and real-world applications of your topic.',
  databaseSearch: 'Database Text Search looks through your previously categorized results. Smart Match finds related content, Exact Phrase matches exact text, Fuzzy Match finds similar terms.'
};

const SearchControls = ({
  searchQuery, setSearchQuery, onSearch, onCategorize, onAutoCategorize,
  onAISearch, onDatabaseSearch, onSearchEngineAccess,
  loading, categorizeLoading, autoCatLoading, aiSearchLoading, dbSearchLoading, searchEngineAccessLoading,
  selectedCategoriesCount, aggregation, setAggregation,
  aiSearchMode, setAiSearchMode, dbSearchMode, setDbSearchMode,
  showMap, setShowMap, mapResultsCount, showDebugger, setShowDebugger, showTemplates, setShowTemplates,
  filterInfo, certificationFilters, setCertificationFilters,
  searchPreferences, setSearchPreferences,
  educatedIndividualFilters, setEducatedIndividualFilters, minWordCount, setMinWordCount,
  eiMinWords, setEiMinWords,
  exactMatch, setExactMatch, searchDepth, setSearchDepth
}) => {
  const [searchEngines, setSearchEngines] = useState(null);
  const [localCertFilters, setLocalCertFilters] = useState({
    pearsonvue: false, government: false, advancedDegree: false
  });
  
  const certFilters = certificationFilters || localCertFilters;
  const setCertFilters = setCertificationFilters || setLocalCertFilters;
  const toggleCertFilter = (filterKey) => {
    setCertFilters(prev => ({ ...prev, [filterKey]: !prev[filterKey] }));
  };
  
  const [isProhibited, setIsProhibited] = useState(false);
  useEffect(() => { setIsProhibited(containsProhibitedContent(searchQuery)); }, [searchQuery]);
  
  const safeAction = (fn) => () => {
    if (containsProhibitedContent(searchQuery)) {
      alert('Content Not Permitted\n\nInfoPilot Explorer is for Officially Approved User-Friendly Content Only.');
      return;
    }
    fn && fn();
  };
  
  useEffect(() => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/search-engines`, true);
    x.onload = function() {
      if (x.status === 200) {
        try { setSearchEngines(JSON.parse(x.responseText)); } catch (e) {}
      }
    };
    x.send();
  }, []);

  const eiTopics = [
    { id: 'science', label: 'Science', desc: 'Chemistry, Physics, Biology', icon: 'fa-flask' },
    { id: 'engineering', label: 'Engineering', desc: 'Electrical, Mechanical, Civil', icon: 'fa-cogs' },
    { id: 'construction', label: 'Construction', desc: 'Framing, Plumbing, HVAC', icon: 'fa-hard-hat' },
    { id: 'culinary', label: 'Culinary', desc: 'Cooking, Recipes, Methods', icon: 'fa-utensils' },
    { id: 'trades', label: 'Trades', desc: 'Welding, Carpentry, Masonry', icon: 'fa-tools' },
    { id: 'mathematics', label: 'Mathematics', desc: 'Calculus, Algebra, Geometry', icon: 'fa-square-root-alt' },
    { id: 'technology', label: 'Technology', desc: 'Programming, Hardware, IT', icon: 'fa-microchip' }
  ];

  return (
    <>
      {/* === BRANDING BANNER === */}
      <div className="ocean-shimmer" style={{
        background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.12), rgba(245, 158, 11, 0.08))',
        borderRadius: 12, padding: '10px 16px', marginBottom: 12,
        border: '1px solid rgba(252, 211, 77, 0.2)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
        position: 'relative'
      }}>
        <SparkleOverlay count={4} />
        <span style={{ color: '#c4b5fd', fontSize: '0.78rem', fontWeight: 600, position: 'relative', zIndex: 4 }}>
          <i className="fas fa-share-alt" style={{ marginRight: 6, color: '#047857' }} />
          Advanced Research Platform & Information Exchange Network
        </span>
        <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US"
          target="_blank" rel="noopener noreferrer"
          style={{ color: '#047857', fontSize: '0.7rem', textDecoration: 'none', fontWeight: 600, position: 'relative', zIndex: 4 }}
          data-testid="book-link-search-bar">
          Powered by Letters to Evelyn
        </a>
      </div>

      {/* Content Policy Warning */}
      {isProhibited && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.15))',
          border: '2px solid rgba(245, 158, 11, 0.5)', borderRadius: 12, padding: '15px 20px', marginBottom: 15,
          display: 'flex', alignItems: 'center', gap: 15
        }}>
          <span style={{ fontSize: '2rem' }}><i className="fas fa-shield-alt" style={{ color: '#f59e0b' }} /></span>
          <div>
            <strong style={{ color: '#f59e0b', fontSize: '1.1rem' }}>Content Not Permitted</strong>
            <p style={{ color: '#fcd34d', margin: '5px 0 0 0', fontSize: '0.9rem' }}>
              InfoPilot Explorer is for Officially Approved User-Friendly Content Only.
            </p>
          </div>
        </div>
      )}
      
      {/* Search Engines Status */}
      {searchEngines && (
        <div className="ocean-shimmer" style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap',
          padding: '8px 12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: 8,
          border: '1px solid rgba(16, 185, 129, 0.2)', position: 'relative'
        }}>
          <span style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 600, position: 'relative', zIndex: 4 }}>
            Search Engines ({searchEngines.total_available} active):
          </span>
          {Object.entries(searchEngines.engines).map(([key, engine]) => (
            <span key={key} className="filter-pill" style={{
              fontSize: '0.7rem', padding: '3px 8px', borderRadius: 4, position: 'relative', zIndex: 4,
              background: engine.available ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: engine.available ? '#10b981' : '#ef4444',
              border: `1px solid ${engine.available ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
            }} title={engine.description}>
              <i className={`fas ${engine.available ? 'fa-check' : 'fa-times'}`} style={{ fontSize: '0.55rem', marginRight: 4 }} />
              {engine.name}
            </span>
          ))}
        </div>
      )}

      {/* === 1. SEARCH AREA === */}
      <div className="search-box">
        <input className="input-field"
          placeholder="Search our Advanced Research Platform (Google, Bing, DuckDuckGo, Brave)..."
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && safeAction(onSearch)()}
          data-testid="search-input" />
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <button className="btn btn-primary btn-ocean" onClick={safeAction(onSearch)} disabled={loading} data-testid="search-btn">
            {loading ? 'Searching...' : 'Quick Search'}
          </button>
          <InfoTooltip text={SEARCH_INFO.quickSearch} />
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <button className="btn btn-primary btn-ocean" onClick={safeAction(onCategorize)} disabled={categorizeLoading}
            style={{ background: selectedCategoriesCount > 0 ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #f59e0b, #d97706)' }}
            data-testid="categorize-btn">
            {categorizeLoading ? 'Categorizing...' : selectedCategoriesCount > 0 ? `Search & Categorize (${selectedCategoriesCount})` : 'Search & Categorize All'}
          </button>
          <InfoTooltip text={SEARCH_INFO.searchCategorize} />
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <button className="btn btn-ocean" onClick={safeAction(onSearchEngineAccess)} disabled={searchEngineAccessLoading || !searchQuery.trim()}
            style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', color: '#0F172A', padding: '10px 16px', fontWeight: 600, opacity: (!searchQuery.trim() || searchEngineAccessLoading) ? 0.5 : 1 }}
            data-testid="search-engine-access-btn" title="Quick search across all engines">
            {searchEngineAccessLoading ? 'Searching...' : 'Search Engine Access'}
          </button>
          <InfoTooltip text={SEARCH_INFO.searchEngineAccess} />
        </span>
      </div>
      
      {/* Search Action Row 1 */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <button className="btn btn-ocean" onClick={safeAction(onAutoCategorize)} disabled={autoCatLoading || !searchQuery.trim()}
            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#0F172A', padding: '10px 20px', fontWeight: 600, opacity: (!searchQuery.trim() || autoCatLoading) ? 0.5 : 1 }}
            data-testid="auto-categorize-btn">
            {autoCatLoading ? 'Categorizing...' : 'Auto-Categorize All'}
          </button>
          <InfoTooltip text={SEARCH_INFO.autoCategorize} />
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <button className="btn btn-ocean" onClick={safeAction(onAISearch)} disabled={aiSearchLoading || !searchQuery.trim()}
            style={{ background: 'linear-gradient(135deg, #FDE047, #FCD34D)', color: '#0F172A', padding: '10px 20px', fontWeight: 600, opacity: (!searchQuery.trim() || aiSearchLoading) ? 0.5 : 1 }}
            data-testid="ai-search-btn">
            {aiSearchLoading ? 'AI Searching...' : 'AI Intelligent Search'}
          </button>
          <InfoTooltip text={SEARCH_INFO.aiSearch} />
          <select value={aiSearchMode || 'comprehensive'} onChange={(e) => setAiSearchMode && setAiSearchMode(e.target.value)}
            style={{ background: 'rgba(252, 211, 77, 0.2)', border: '1px solid rgba(252, 211, 77, 0.4)', color: '#047857', padding: '8px 12px', borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer' }}
            data-testid="ai-search-mode">
            <option value="comprehensive">Comprehensive</option>
            <option value="news">News Focus</option>
            <option value="research">Research Focus</option>
          </select>
          <InfoTooltip text={SEARCH_INFO.comprehensive} />
        </div>
      </div>
      
      {/* Search Action Row 2 — Database Search centered around EI content */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 15, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <button className="btn btn-ocean" onClick={safeAction(onDatabaseSearch)} disabled={dbSearchLoading || !searchQuery.trim()}
            style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', color: '#0F172A', padding: '10px 20px', fontWeight: 600, opacity: (!searchQuery.trim() || dbSearchLoading) ? 0.5 : 1 }}
            data-testid="database-search-btn">
            {dbSearchLoading ? 'Searching...' : 'Database Text Search'}
          </button>
          <InfoTooltip text={SEARCH_INFO.databaseSearch} />
          <select value={dbSearchMode || 'smart'} onChange={(e) => setDbSearchMode && setDbSearchMode(e.target.value)}
            style={{ background: 'rgba(6, 182, 212, 0.2)', border: '1px solid rgba(6, 182, 212, 0.4)', color: '#22d3ee', padding: '8px 12px', borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer' }}
            data-testid="db-search-mode">
            <option value="smart">Smart Match</option>
            <option value="exact">Exact Phrase</option>
            <option value="fuzzy">Fuzzy Match</option>
          </select>
        </div>
        <span style={{ color: '#a1a1aa', fontSize: '0.75rem', maxWidth: 450 }}>
          <strong>AI Search:</strong> Google, Bing, DuckDuckGo, Brave with GPT expansion.
          <strong> Database:</strong> Finds in your categorized results.
        </span>
      </div>

      {/* ================================================================ */}
      {/* === PRIMARY: EDUCATED INDIVIDUAL SITES (nature-sky accent) === */}
      {/* ================================================================ */}
      {educatedIndividualFilters && setEducatedIndividualFilters && (
        <div className="ocean-shimmer-strong" style={{
          marginBottom: 14, padding: 20, borderRadius: 16,
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.08), rgba(16, 185, 129, 0.12), rgba(134, 239, 172, 0.10))',
          border: '2px solid rgba(16, 185, 129, 0.35)',
          position: 'relative'
        }} data-testid="educated-individual-sites-section">
          <SparkleOverlay count={8} />
          <div style={{ position: 'relative', zIndex: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <h4 className="shimmer-text" style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, color: '#047857' }}>
                <i className="fas fa-graduation-cap" style={{ color: '#059669' }} />
                Educated Individual Sites
                <span style={{
                  background: 'linear-gradient(135deg, #10B981, #047857)',
                  padding: '3px 10px', borderRadius: 10, fontSize: '0.58rem', color: '#ffffff', fontWeight: 700, letterSpacing: '0.06em',
                  WebkitBackgroundClip: 'unset', WebkitTextFillColor: 'unset', backgroundClip: 'unset'
                }}>COMPREHENSIVE CONTENT</span>
              </h4>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ color: '#a1a1aa', fontSize: '0.72rem' }}>Global min:</span>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={minWordCount || 4000}
                    onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setMinWordCount && setMinWordCount(Math.min(100000, Math.max(0, parseInt(v) || 0))); }}
                    onFocus={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    className="word-count-input"
                    data-testid="ei-min-word-count"
                    style={{
                      width: 72, background: 'rgba(253, 230, 138, 0.12)', border: '1px solid rgba(253, 230, 138, 0.35)',
                      borderRadius: 6, padding: '4px 8px', color: '#38bdf8', fontSize: '0.78rem', textAlign: 'center'
                    }} />
                </div>
                <button onClick={() => setEducatedIndividualFilters(prev => ({
                  ...prev, science: true, engineering: true, construction: true, culinary: true, trades: true, mathematics: true, technology: true
                }))} data-testid="ei-select-all" className="btn-ocean"
                  style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#10b981', padding: '4px 10px', borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer' }}>
                  All Topics
                </button>
                <button onClick={() => setEducatedIndividualFilters(prev => ({
                  ...prev, science: false, engineering: false, construction: false, culinary: false, trades: false, mathematics: false, technology: false
                }))} data-testid="ei-clear-all" className="btn-ocean"
                  style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', padding: '4px 10px', borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer' }}>
                  Clear
                </button>
              </div>
            </div>

            {/* EI Topic pills with per-topic word counts */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {eiTopics.map(topic => (
                <div key={topic.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label className={`filter-pill ${educatedIndividualFilters[topic.id] ? 'filter-pill-active' : ''}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 12, cursor: 'pointer',
                      background: educatedIndividualFilters[topic.id] ? 'rgba(253, 230, 138, 0.15)' : '#475569',
                      border: educatedIndividualFilters[topic.id] ? '2px solid rgba(253, 230, 138, 0.45)' : '1px solid rgba(203, 213, 225, 0.8)'
                    }} title={topic.desc} data-testid={`educated-individual-${topic.id}`}>
                    <input type="checkbox" checked={educatedIndividualFilters[topic.id]}
                      onChange={() => setEducatedIndividualFilters(prev => ({ ...prev, [topic.id]: !prev[topic.id] }))}
                      style={{ accentColor: '#38bdf8' }} />
                    <i className={`fas ${topic.icon}`} style={{
                      fontSize: '0.7rem', color: educatedIndividualFilters[topic.id] ? '#38bdf8' : '#555'
                    }} />
                    <span style={{
                      color: educatedIndividualFilters[topic.id] ? '#e0f2fe' : '#a1a1aa',
                      fontSize: '0.84rem', fontWeight: educatedIndividualFilters[topic.id] ? 700 : 400
                    }}>
                      {topic.label}
                    </span>
                  </label>
                  {eiMinWords && setEiMinWords && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, paddingLeft: 6 }}>
                      <span style={{ color: '#555', fontSize: '0.58rem' }}>min:</span>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" value={eiMinWords[topic.id] || 0}
                        onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setEiMinWords(prev => ({ ...prev, [topic.id]: Math.max(0, parseInt(v) || 0) })); }}
                        onFocus={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        className="word-count-input"
                        data-testid={`ei-min-words-${topic.id}`}
                        style={{
                          width: 60, background: 'rgba(253, 230, 138, 0.08)', border: '1px solid rgba(253, 230, 138, 0.2)',
                          borderRadius: 5, padding: '2px 5px', color: '#38bdf8', fontSize: '0.65rem', textAlign: 'center'
                        }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Favor Visuals & Favor Pictures */}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label className={`filter-pill ${educatedIndividualFilters.favor_visuals ? 'filter-pill-active' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px',
                  background: educatedIndividualFilters.favor_visuals ? 'rgba(34, 211, 238, 0.15)' : '#475569',
                  border: educatedIndividualFilters.favor_visuals ? '2px solid #22d3ee' : '1px solid rgba(203, 213, 225, 0.8)',
                  borderRadius: 10, cursor: 'pointer'
                }} data-testid="educated-individual-favor-visuals">
                <input type="checkbox" checked={educatedIndividualFilters.favor_visuals}
                  onChange={() => setEducatedIndividualFilters(prev => ({ ...prev, favor_visuals: !prev.favor_visuals }))}
                  style={{ accentColor: '#22d3ee' }} />
                <i className="fas fa-drafting-compass" style={{ color: educatedIndividualFilters.favor_visuals ? '#22d3ee' : '#555', fontSize: '0.7rem' }} />
                <span style={{ color: educatedIndividualFilters.favor_visuals ? '#22d3ee' : '#a1a1aa', fontSize: '0.8rem', fontWeight: educatedIndividualFilters.favor_visuals ? 600 : 400 }}>
                  Favor Diagrams, Drawings & Schematics
                </span>
              </label>
              <label className={`filter-pill ${educatedIndividualFilters.favor_pictures ? 'filter-pill-active' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px',
                  background: educatedIndividualFilters.favor_pictures ? 'rgba(253, 230, 138, 0.15)' : '#475569',
                  border: educatedIndividualFilters.favor_pictures ? '2px solid rgba(253, 230, 138, 0.45)' : '1px solid rgba(203, 213, 225, 0.8)',
                  borderRadius: 10, cursor: 'pointer'
                }} data-testid="educated-individual-favor-pictures">
                <input type="checkbox" checked={educatedIndividualFilters.favor_pictures || false}
                  onChange={() => setEducatedIndividualFilters(prev => ({ ...prev, favor_pictures: !prev.favor_pictures }))}
                  style={{ accentColor: '#38bdf8' }} />
                <i className="fas fa-images" style={{ color: educatedIndividualFilters.favor_pictures ? '#38bdf8' : '#555', fontSize: '0.7rem' }} />
                <span style={{ color: educatedIndividualFilters.favor_pictures ? '#e0f2fe' : '#a1a1aa', fontSize: '0.8rem', fontWeight: educatedIndividualFilters.favor_pictures ? 600 : 400 }}>
                  Favor Pictures & Photos
                </span>
              </label>
            </div>

            <p style={{ color: '#71717a', fontSize: '0.72rem', marginTop: 12, lineHeight: 1.6 }}>
              Comprehensive sites from educated professors, experts, and self-educated web builders sharing
              <strong style={{ color: '#38bdf8' }}> extensively and accurately written</strong> text, diagrams, and schematics needed to become proficient on any topic.
              <strong style={{ color: '#38bdf8' }}> Minimum {minWordCount || 4000} words</strong> for depth and precision.
            </p>
          </div>
        </div>
      )}

      {/* === 3. CONTENT PREFERENCE FILTERS === */}
      <ContentPreferenceFiltersCollapsible certFilters={certFilters} setCertFilters={setCertFilters} toggleCertFilter={toggleCertFilter} />

      {/* === 4. CERTIFICATION FILTERS (SECONDARY — collapsed, unchecked by default) === */}
      <CertificationFiltersCollapsible certFilters={certFilters} setCertFilters={setCertFilters} toggleCertFilter={toggleCertFilter} />

      {/* === 5. CATEGORY LOGIC === */}
      <div className="ocean-shimmer" style={{ display: 'flex', gap: 20, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', borderRadius: 10, position: 'relative', border: '1px solid rgba(252, 211, 77,0.1)' }}>
        <span style={{ color: '#a1a1aa', position: 'relative', zIndex: 4 }}>Category Logic:</span>
        {[
          { value: 'and_or', label: 'AND/OR', desc: 'Match any category' },
          { value: 'and', label: 'AND', desc: 'Match ALL categories' },
          { value: 'or', label: 'OR', desc: 'Match any category' }
        ].map(agg => (
          <label key={agg.value} className="filter-pill" style={{
            display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', padding: '6px 12px', borderRadius: 8,
            background: aggregation === agg.value ? 'rgba(252, 211, 77, 0.3)' : 'transparent',
            border: aggregation === agg.value ? '1px solid rgba(252, 211, 77, 0.5)' : '1px solid transparent',
            position: 'relative', zIndex: 4
          }} title={agg.desc} data-testid={`aggregation-${agg.value}`}>
            <input type="radio" name="aggregation" checked={aggregation === agg.value} onChange={() => setAggregation(agg.value)} style={{ accentColor: '#FCD34D' }} />
            <span style={{ color: aggregation === agg.value ? '#047857' : '#9ca3af' }}>{agg.label}</span>
          </label>
        ))}
        {selectedCategoriesCount > 0 && (
          <span style={{ padding: '4px 10px', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', borderRadius: 6, fontSize: '0.8rem', position: 'relative', zIndex: 4 }}>
            Filtering by {selectedCategoriesCount} categor{selectedCategoriesCount > 1 ? 'ies' : 'y'} ({aggregation.toUpperCase().replace('_', '/')})
          </span>
        )}
      </div>

      {/* === 6. EXACT MATCH TOGGLE === */}
      {setExactMatch && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} data-testid="exact-match-toggle">
            <div className="filter-pill" style={{
              width: 40, height: 22, borderRadius: 11, position: 'relative', cursor: 'pointer',
              background: exactMatch ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255,255,255,0.15)',
              border: exactMatch ? '1px solid #10b981' : '1px solid rgba(255,255,255,0.2)'
            }} onClick={() => setExactMatch(!exactMatch)}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
                left: exactMatch ? 21 : 3, transition: 'left 0.2s'
              }} />
            </div>
            <span style={{ color: exactMatch ? '#10b981' : '#a1a1aa', fontSize: '0.85rem', fontWeight: exactMatch ? 600 : 400 }}>
              Exact Match
            </span>
          </label>
          
          {/* === 7. SEARCH DEPTH === */}
          {setSearchDepth && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>Search Depth:</span>
              <select value={searchDepth || 'standard'} onChange={(e) => setSearchDepth(e.target.value)}
                style={{
                  background: 'rgba(252, 211, 77, 0.15)', border: '1px solid rgba(252, 211, 77, 0.3)',
                  color: '#047857', padding: '6px 12px', borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer'
                }} data-testid="search-depth-select">
                <option value="shallow">Shallow (Fast)</option>
                <option value="standard">Standard</option>
                <option value="deep">Deep (Thorough)</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Map/Debugger/Templates Toggles */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className={`btn btn-ocean ${showMap ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowMap(!showMap)}
          style={{ padding: '8px 16px', fontSize: '0.85rem' }} data-testid="toggle-map-btn">
          {showMap ? 'Hide' : 'Show'} Map ({mapResultsCount} locations)
        </button>
        <button className={`btn btn-ocean ${showDebugger ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowDebugger(!showDebugger)}
          style={{ padding: '8px 16px', fontSize: '0.85rem' }} data-testid="toggle-debugger-btn">
          {showDebugger ? 'Hide' : 'Show'} Protocol Debugger
        </button>
        <button className={`btn btn-ocean ${showTemplates ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowTemplates(!showTemplates)}
          style={{ padding: '8px 16px', fontSize: '0.85rem' }} data-testid="toggle-templates-btn">
          {showTemplates ? 'Hide' : 'Show'} Templates
        </button>
      </div>
    </>
  );
};

export default SearchControls;
